import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui";
import type { ColSpec } from "../grid/columns";
export interface ViewConfig { params: Record<string, string>; hidden: string[] }
export interface SavedView { id: string; user_id: string; name: string; owner_name: string; config: ViewConfig; team_id: string | null; is_default: boolean }
const KEYS = ["scope", "month", "tier", "q", "group", "closed", "sort"];
export function captureView(params: URLSearchParams, defaults: Record<string, string> = {}): ViewConfig {
  return { params: { ...defaults, ...Object.fromEntries(KEYS.filter((k) => params.has(k)).map((k) => [k, params.get(k)!])) }, hidden: (params.get("hidden") ?? "").split(",").filter(Boolean) };
}
export function applyView(config: ViewConfig, current: URLSearchParams, id: string): URLSearchParams {
  const next = new URLSearchParams(config.params);
  if (config.hidden.length) next.set("hidden", config.hidden.join(","));
  if (current.has("loan")) next.set("loan", current.get("loan")!);
  next.set("view", id);
  return next;
}
export function SavedViews({ userId, params, onParams, columns, defaults }: { userId: string; params: URLSearchParams; onParams: (p: URLSearchParams) => void; columns: ColSpec[]; defaults: Record<string, string> }) {
  const qc = useQueryClient();
  const views = useQuery({ queryKey: ["views", userId], queryFn: () => api<SavedView[]>("/api/views") });
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const [makeDefault, setDefault] = useState(false);
  const appliedDefault = useRef(false);
  useEffect(() => {
    if (appliedDefault.current || !views.data) return;
    appliedDefault.current = true;
    if (KEYS.some((k) => params.has(k)) || params.has("hidden") || params.has("view")) return;
    const view = views.data.find((v) => v.user_id === userId && v.is_default);
    if (view) onParams(applyView(view.config, params, view.id));
  }, [views.data, params, onParams, userId]);
  const selected = views.data?.find((v) => v.id === params.get("view"));
  const own = selected?.user_id === userId;
  const save = async (method: string, path: string, body?: unknown) => {
    try {
      const view = await api<SavedView | undefined>(path, { method, body });
      await qc.invalidateQueries({ queryKey: ["views"] });
      if (method === "POST" && view) onParams(applyView(view.config, params, view.id));
      if (method === "DELETE") { const next = new URLSearchParams(params); next.delete("view"); onParams(next); }
      toast.success("Saved views updated");
    } catch (e) { toast.error(e instanceof Error ? e.message : "Couldn't save view"); }
  };
  return <details className="relative text-[13px]">
    <summary className="focus-ring cursor-pointer rounded-md border border-input px-3 py-1.5">Views & columns</summary>
    <div className="absolute right-0 z-30 mt-2 w-80 space-y-3 rounded-md border border-line bg-raised p-3 shadow-lg">
      <label className="block">Saved view
        <select aria-label="Saved view" value={selected?.id ?? ""} className="mt-1 w-full rounded border border-input bg-surface p-2" onChange={(e) => {
          const v = views.data?.find((v) => v.id === e.target.value);
          if (v) { onParams(applyView(v.config, params, v.id)); setName(v.name); }
          else { const next = new URLSearchParams(params); next.delete("view"); onParams(next); }
        }}>
          <option value="">Current view</option>
          {views.data?.map((v) => <option key={v.id} value={v.id}>{v.name}{v.is_default && v.user_id === userId ? " ★" : ""}{v.user_id !== userId ? ` · ${v.owner_name} (shared)` : ""}</option>)}
        </select>
      </label>
      {selected && !own ? <p>Shared view · read-only. Save a personal copy to customize it.</p> : null}
      <input aria-label="View name" value={name} placeholder="View name" onChange={(e) => setName(e.target.value)} className="w-full rounded border border-input bg-surface p-2" maxLength={100} />
      <label className="flex gap-2"><input type="checkbox" checked={makeDefault} onChange={(e) => setDefault(e.target.checked)} />Make new view default</label>
      <label className="flex gap-2"><input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />Share new view with my team</label>
      <Button disabled={!name.trim()} onClick={() => void save("POST", "/api/views", { name, config: captureView(params, defaults), is_default: makeDefault, share_with_team: shared })}>Save current view</Button>
      {own && selected ? <div className="flex flex-wrap gap-2">
        <Button disabled={!name.trim()} onClick={() => void save("PATCH", `/api/views/${selected.id}`, { name })}>Rename</Button>
        <Button onClick={() => void save("PATCH", `/api/views/${selected.id}`, { is_default: !selected.is_default })}>{selected.is_default ? "Unset default" : "Set default"}</Button>
        <Button onClick={() => void save("PATCH", `/api/views/${selected.id}`, { share_with_team: !selected.team_id })}>{selected.team_id ? "Unshare" : "Share"}</Button>
        <Button onClick={() => void save("DELETE", `/api/views/${selected.id}`)}>Delete view</Button>
      </div> : null}
      <fieldset><legend className="font-semibold">Visible columns</legend>
        <div className="max-h-48 overflow-auto">{columns.map((c) => {
          const hidden = captureView(params).hidden;
          return <label key={c.id} className="flex gap-2 py-1"><input type="checkbox" disabled={c.id === "file_name"} checked={!hidden.includes(c.id) || c.id === "file_name"} onChange={(e) => {
            const next = new URLSearchParams(params);
            const ids = e.target.checked ? hidden.filter((id) => id !== c.id) : [...hidden, c.id];
            if (ids.length) next.set("hidden", ids.join(",")); else next.delete("hidden");
            onParams(next);
          }} />{c.title}</label>;
        })}</div>
      </fieldset>
    </div>
  </details>;
}
